import React, { useState, useEffect, Fragment } from 'react';
import { Settings, Plus, Edit2, Trash2, Eye, List, ArrowUp, ArrowDown } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const EvaluationManagement = () => {
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('groups'); // 'groups', 'items', 'preview'
  
  // Pagination state for items
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form states
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  
  const [groupFormData, setGroupFormData] = useState({
    evaluation_group: '',
    sort_order: 0
  });

  const [itemFormData, setItemFormData] = useState({
    evaluation_item: '',
    evaluation_group_id: '',
    is_default: true,
    sort_order: 0,
    rating_scale: 5
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [groupsRes, itemsRes] = await Promise.all([
        api.get('/simple.php?action=evaluation-groups'),
        api.get('/simple.php?action=evaluation-items')
      ]);
      
      setGroups(groupsRes.data.data);
      setItems(itemsRes.data.data);
    } catch (error) {
      console.error('Error fetching evaluation data:', error);
      toast.error('Failed to fetch evaluation data');
    } finally {
      setLoading(false);
    }
  };

  // Group Management Functions
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    if (!groupFormData.evaluation_group.trim()) {
      toast.error('Group name is required');
      return;
    }

    try {
      if (editingGroup) {
        await api.post(`/simple.php?action=evaluation-groups&id=${editingGroup.evaluation_group_id}&_method=PUT`, groupFormData);
        toast.success('Evaluation group updated successfully!');
      } else {
        await api.post('/simple.php?action=evaluation-groups', groupFormData);
        toast.success('Group created successfully!');
      }
      
      await fetchData();
      setShowGroupForm(false);
      setEditingGroup(null);
      setGroupFormData({ evaluation_group: '', sort_order: 0 });
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error(error.response?.data?.message || 'Failed to save group');
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupFormData({
      evaluation_group: group.evaluation_group,
      sort_order: group.sort_order
    });
    setShowGroupForm(true);
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    const itemsInGroup = items.filter(item => item.evaluation_group_id === groupId);
    if (itemsInGroup.length > 0) {
      toast.error(`Cannot delete group "${groupName}" because it contains ${itemsInGroup.length} evaluation items. Delete the items first.`);
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${groupName}"?`)) {
      try {
        await api.post(`/simple.php?action=evaluation-groups&id=${groupId}&_method=DELETE`);
        toast.success('Group deleted successfully!');
        fetchData();
      } catch (error) {
        console.error('Error deleting group:', error);
        toast.error('Failed to delete group');
      }
    }
  };

  // Item Management Functions
  const handleItemSubmit = async (e) => {
    e.preventDefault();
    if (!itemFormData.evaluation_item.trim() || !itemFormData.evaluation_group_id) {
      toast.error('Item text and group are required');
      return;
    }

    try {
      if (editingItem) {
        await api.post(`/simple.php?action=evaluation-items&id=${editingItem.evaluation_item_id}&_method=PUT`, itemFormData);
        toast.success('Evaluation item updated successfully!');
      } else {
        await api.post('/simple.php?action=evaluation-items', itemFormData);
        toast.success('Item created successfully!');
      }
      
      await fetchData();
      setShowItemForm(false);
      setEditingItem(null);
      setItemFormData({
        evaluation_item: '',
        evaluation_group_id: '',
        is_default: true,
        sort_order: 0,
        rating_scale: 5
      });
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(error.response?.data?.message || 'Failed to save item');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemFormData({
      evaluation_item: item.evaluation_item,
      evaluation_group_id: item.evaluation_group_id,
      is_default: item.is_default,
      sort_order: item.sort_order,
      rating_scale: item.rating_scale
    });
    setShowItemForm(true);
  };

  const handleDeleteItem = async (itemId, itemText) => {
    if (window.confirm(`Are you sure you want to delete this evaluation item?`)) {
      try {
        await api.post(`/simple.php?action=evaluation-items&id=${itemId}&_method=DELETE`);
        toast.success('Item deleted successfully!');
        fetchData();
      } catch (error) {
        console.error('Error deleting item:', error);
        toast.error('Failed to delete item');
      }
    }
  };

  // Pagination helpers
  const getFilteredItems = () => {
    return items.filter(item => 
      item.evaluation_item.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.evaluation_group.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getPaginatedItems = () => {
    const filtered = getFilteredItems();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = () => {
    return Math.ceil(getFilteredItems().length / itemsPerPage);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Preview Functions
  const getGroupedItems = () => {
    const groupedItems = {};
    
    groups.forEach(group => {
      groupedItems[group.evaluation_group_id] = {
        ...group,
        items: items.filter(item => 
          item.evaluation_group_id === group.evaluation_group_id && item.is_default
        ).sort((a, b) => a.sort_order - b.sort_order)
      };
    });

    return Object.values(groupedItems)
      .filter(group => group.items.length > 0)
      .sort((a, b) => a.sort_order - b.sort_order);
  };

  if (loading && groups.length === 0) {
    return (
      <div className="container" style={{ padding: '40px 20px' }}>
        <div className="loading">Loading evaluation management...</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '40px 20px' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ color: '#1f2937', marginBottom: '8px' }}>Evaluation Management</h1>
        <p style={{ color: '#6b7280' }}>
          Configure evaluation groups and items for the evaluation forms
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        gap: '4px', 
        marginBottom: '32px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <button
          className={`btn ${activeTab === 'groups' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ borderRadius: '6px 6px 0 0' }}
          onClick={() => setActiveTab('groups')}
        >
          <Settings size={16} />
          Groups ({groups.length})
        </button>
        <button
          className={`btn ${activeTab === 'items' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ borderRadius: '6px 6px 0 0' }}
          onClick={() => setActiveTab('items')}
        >
          <List size={16} />
          Items ({items.length})
        </button>
        <button
          className={`btn ${activeTab === 'preview' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ borderRadius: '6px 6px 0 0' }}
          onClick={() => setActiveTab('preview')}
        >
          <Eye size={16} />
          Preview
        </button>
      </div>

      {/* Groups Tab */}
      {activeTab === 'groups' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3>Evaluation Groups</h3>
            <button 
              className="btn btn-primary"
              onClick={() => setShowGroupForm(true)}
            >
              <Plus size={16} />
              Add Group
            </button>
          </div>

          {/* Group Form */}
          {showGroupForm && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h4>{editingGroup ? 'Edit Group' : 'Add New Group'}</h4>
              <form onSubmit={handleGroupSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Group Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={groupFormData.evaluation_group}
                      onChange={(e) => setGroupFormData({...groupFormData, evaluation_group: e.target.value})}
                      placeholder="e.g., Presentation Quality, Venue & Facilities"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sort Order</label>
                    <input
                      type="number"
                      className="form-input"
                      value={groupFormData.sort_order}
                      onChange={(e) => setGroupFormData({...groupFormData, sort_order: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowGroupForm(false);
                      setEditingGroup(null);
                      setGroupFormData({ evaluation_group: '', sort_order: 0 });
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingGroup ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Groups List */}
          <div className="card">
            {groups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <Settings size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>No evaluation groups found. Create your first group to get started.</p>
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Group Name</th>
                    <th>Items Count</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.sort((a, b) => a.sort_order - b.sort_order).map((group) => (
                    <tr key={group.evaluation_group_id}>
                      <td>{group.sort_order}</td>
                      <td><strong>{group.evaluation_group}</strong></td>
                      <td>
                        {items.filter(item => item.evaluation_group_id === group.evaluation_group_id).length} items
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-primary"
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                            onClick={() => handleEditGroup(group)}
                          >
                            <Edit2 size={12} />
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                            onClick={() => handleDeleteGroup(group.evaluation_group_id, group.evaluation_group)}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Items Tab */}
      {activeTab === 'items' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3>Evaluation Items</h3>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {/* Search Input */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search items..."
                  className="form-input"
                  style={{ width: '200px', paddingRight: '32px' }}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
              
              <button 
                className="btn btn-secondary"
                onClick={() => setShowItemForm(true)}
                disabled={groups.length === 0}
              >
                <Plus size={16} />
                Add Item
              </button>
            </div>
          </div>

          {groups.length === 0 && (
            <div className="card" style={{ marginBottom: '24px', background: '#fef3c7', border: '1px solid #f59e0b' }}>
              <p style={{ margin: 0, color: '#92400e' }}>
                ⚠️ You need to create at least one evaluation group before adding items.
              </p>
            </div>
          )}

          {/* Item Form */}
          {showItemForm && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h4>{editingItem ? 'Edit Item' : 'Add New Item'}</h4>
              <form onSubmit={handleItemSubmit}>
                <div className="form-group">
                  <label className="form-label">Evaluation Item Text</label>
                  <textarea
                    className="form-input"
                    rows="2"
                    value={itemFormData.evaluation_item}
                    onChange={(e) => setItemFormData({...itemFormData, evaluation_item: e.target.value})}
                    placeholder="e.g., How would you rate the clarity of the speaker's presentation?"
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Group</label>
                    <select
                      className="form-input"
                      value={itemFormData.evaluation_group_id}
                      onChange={(e) => setItemFormData({...itemFormData, evaluation_group_id: e.target.value})}
                      required
                    >
                      <option value="">Select a group</option>
                      {groups.map(group => (
                        <option key={group.evaluation_group_id} value={group.evaluation_group_id}>
                          {group.evaluation_group}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Rating Scale</label>
                    <select
                      className="form-input"
                      value={itemFormData.rating_scale}
                      onChange={(e) => setItemFormData({...itemFormData, rating_scale: parseInt(e.target.value)})}
                    >
                      <option value={3}>1-3 Scale</option>
                      <option value={5}>1-5 Scale</option>
                      <option value={10}>1-10 Scale</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Sort Order</label>
                    <input
                      type="number"
                      className="form-input"
                      value={itemFormData.sort_order}
                      onChange={(e) => setItemFormData({...itemFormData, sort_order: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={itemFormData.is_default}
                      onChange={(e) => setItemFormData({...itemFormData, is_default: e.target.checked})}
                    />
                    <span className="form-label" style={{ margin: 0 }}>Include in all evaluations (default)</span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowItemForm(false);
                      setEditingItem(null);
                      setItemFormData({
                        evaluation_item: '',
                        evaluation_group_id: '',
                        is_default: true,
                        sort_order: 0,
                        rating_scale: 5
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-secondary">
                    {editingItem ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Items List */}
          <div className="card">
            {items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <List size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>No evaluation items found. Add your first item to get started.</p>
              </div>
            ) : getFilteredItems().length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <List size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>No items match your search criteria.</p>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleSearch('')}
                  style={{ marginTop: '16px' }}
                >
                  Clear Search
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <p style={{ color: '#6b7280', margin: 0 }}>
                    Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, getFilteredItems().length)} of {getFilteredItems().length} items
                  </p>
                </div>
                
                <div style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Item Text</th>
                        <th>Group</th>
                        <th>Scale</th>
                        <th>Default</th>
                        <th>Order</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems().map((item) => (
                        <tr key={item.evaluation_item_id}>
                          <td style={{ maxWidth: '300px' }}>
                            {item.evaluation_item.length > 100 ? 
                              item.evaluation_item.substring(0, 100) + '...' : 
                              item.evaluation_item
                            }
                          </td>
                          <td>{item.evaluation_group}</td>
                          <td>1-{item.rating_scale}</td>
                          <td>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              background: item.is_default ? '#dcfce7' : '#fef2f2',
                              color: item.is_default ? '#166534' : '#dc2626'
                            }}>
                              {item.is_default ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>{item.sort_order}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                className="btn btn-primary"
                                style={{ padding: '4px 6px', fontSize: '10px' }}
                                onClick={() => handleEditItem(item)}
                              >
                                <Edit2 size={10} />
                              </button>
                              <button
                                className="btn btn-danger"
                                style={{ padding: '4px 6px', fontSize: '10px' }}
                                onClick={() => handleDeleteItem(item.evaluation_item_id, item.evaluation_item)}
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {getTotalPages() > 1 && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '20px',
                    padding: '16px 0',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '8px 12px', fontSize: '12px' }}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: getTotalPages() }, (_, i) => i + 1)
                      .filter(page => {
                        // Show first page, last page, current page, and pages around current
                        return page === 1 || 
                               page === getTotalPages() || 
                               Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, index, array) => {
                        // Add ellipsis if there's a gap
                        const prevPage = array[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;
                        
                        return (
                          <Fragment key={page}>
                            {showEllipsis && (
                              <span style={{ padding: '8px 4px', color: '#6b7280' }}>...</span>
                            )}
                            <button
                              className={`btn ${page === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                              style={{ 
                                padding: '8px 12px', 
                                fontSize: '12px',
                                minWidth: '36px'
                              }}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </Fragment>
                        );
                      })
                    }
                    
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '8px 12px', fontSize: '12px' }}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === getTotalPages()}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div>
          <h3 style={{ marginBottom: '24px' }}>Evaluation Form Preview</h3>
          
          {getGroupedItems().length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <Eye size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
              <h4 style={{ color: '#6b7280', marginBottom: '8px' }}>No Preview Available</h4>
              <p style={{ color: '#9ca3af' }}>
                Create evaluation groups and items to see how your evaluation form will look.
              </p>
            </div>
          ) : (
            <div>
              {getGroupedItems().map((group, groupIndex) => (
                <div key={group.evaluation_group_id} className="evaluation-group">
                  <h3>{groupIndex + 1}. {group.evaluation_group}</h3>

                  {group.items.map((item, itemIndex) => (
                    <div key={item.evaluation_item_id} className="evaluation-item">
                      <div style={{ marginBottom: '12px' }}>
                        <label className="form-label" style={{ fontSize: '15px', fontWeight: '500' }}>
                          {groupIndex + 1}.{itemIndex + 1} {item.evaluation_item}
                        </label>
                      </div>

                      <div className="rating-group">
                        {[...Array(item.rating_scale)].map((_, i) => (
                          <div
                            key={i + 1}
                            className="rating-option"
                            style={{ cursor: 'not-allowed', opacity: 0.7 }}
                          >
                            {i + 1}
                          </div>
                        ))}
                      </div>

                      <div className="form-group" style={{ marginTop: '12px' }}>
                        <label className="form-label" style={{ fontSize: '13px', color: '#6b7280' }}>
                          Additional Comments (Optional)
                        </label>
                        <textarea
                          className="form-input"
                          rows="2"
                          placeholder="Comments section..."
                          disabled
                          style={{ backgroundColor: '#f9fafb', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              <div className="card" style={{ marginTop: '24px', background: '#f0fdf4', border: '1px solid #22c55e' }}>
                <p style={{ margin: 0, color: '#15803d' }}>
                  ✅ This is how your evaluation form will appear to public users. 
                  Only items marked as "default" are shown in the preview.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EvaluationManagement;